import { Box, Button, Tooltip, Typography } from '@mui/material';
import { useState } from 'react';
import { SsbEntryPoint } from '../../types/enums/SsbEntryPoint.enum';
import { SsbCondition } from '../../types/SimilarlySituated/SsbCondition.type';
import { MAX_CONDITIONS } from '../../utils/ssbBuilderBox';
import ConditionChip from './ConditionChip';
import ConditionPanel from './ConditionPanel';

const addButtonStyle = {
  borderRadius: '24px',
  border: '1px dashed #FECACA',
  color: '#B91C1C',
  fontSize: '12px',
  lineHeight: '18px',
  padding: '6px 12px',
  ':hover': {
    backgroundColor: '#FEF2F2',
  },
};

const sectionLabelStyle = {
  fontSize: '11px',
  fontWeight: 600,
  color: '#718096',
  textTransform: 'uppercase',
};

interface BoxWideTraitsCardProps {
  traits: SsbCondition[];
  canAddTraits: boolean;
  countyId?: number | null;
  onChange: (traits: SsbCondition[]) => void;
}

function BoxWideTraitsCard({ traits, canAddTraits, countyId, onChange }: BoxWideTraitsCardProps) {
  const [panel, setPanel] = useState<{ editing?: SsbCondition } | null>(null);
  const canAddTrait = traits.length < MAX_CONDITIONS;

  const removeTrait = (traitId: string) => {
    // close the editing panel on chip removal
    if (panel?.editing?.id === traitId) setPanel(null);

    onChange(traits.filter((existing) => existing.id !== traitId));
  };

  const handleCommit = (condition: SsbCondition) => {
    const exists = traits.some((existing) => existing.id === condition.id);
    onChange(
      exists ? traits.map((existing) => (existing.id === condition.id ? condition : existing)) : [...traits, condition],
    );
  };

  return (
    <Box sx={{ opacity: canAddTraits ? 1 : 0.5 }}>
      <Box
        sx={{
          backgroundColor: '#fff',
          border: '1px solid #CBD5E0',
          borderRadius: '8px',
          boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)',
          overflow: 'hidden',
          pointerEvents: canAddTraits ? 'auto' : 'none',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Box sx={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#B91C1C' }}>
            + add a box-wide exclusion
          </Typography>
          <Typography sx={{ ...sectionLabelStyle, color: '#B91C1C', marginTop: '12px' }}>
            Box Does{' '}
            <Typography component={'span'} sx={{ fontSize: 'inherit', fontWeight: 800 }}>
              NOT
            </Typography>{' '}
            include
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px' }}>
            {traits.map((condition) => (
              <ConditionChip
                key={condition.id}
                condition={condition}
                variant='except'
                onEdit={() => setPanel({ editing: condition })}
                onRemove={() => removeTrait(condition.id)}
              />
            ))}
            <Tooltip title={canAddTrait ? '' : `Maximum number of box-wide trait conditions reached.`}>
              <span>
                <Button
                  disabled={!canAddTrait}
                  sx={{ ...addButtonStyle, ':disabled': { color: '#FEB2B2' } }}
                  onClick={() => setPanel({})}
                >
                  + except
                </Button>
              </span>
            </Tooltip>
          </Box>

          {panel && (
            <ConditionPanel
              key={panel.editing?.id ?? 'new'}
              entryPoint={SsbEntryPoint.BOX_WIDE}
              countyId={countyId}
              editingCondition={panel.editing}
              conflicts={[{ conditions: traits, message: 'This condition is already a box-wide trait.' }]}
              onCommit={handleCommit}
              onClose={() => setPanel(null)}
            />
          )}
        </Box>
      </Box>
      <Typography
        sx={{
          backgroundColor: '#2C5282',
          color: '#fff',
          fontSize: '11px',
          fontWeight: 700,
          letterSpacing: '0.08em',
          marginTop: '-14px',
          padding: '20px 24px 5px',
          borderRadius: '0 0 8px 8px',
        }}
      >
        BOX-WIDE TRAITS
      </Typography>
    </Box>
  );
}

export default BoxWideTraitsCard;
